<template>
  <div class="page">
    <van-nav-bar title="客户列表" left-text="返回" left-arrow @click-left="$router.back()"
                 right-text="新增" @click-right="goAdd"/>

    <!-- 搜索框 -->
    <div class="search-bar">
      <input class="search-input" v-model="keyword" @keyup.enter="resetAndLoad"
             placeholder="请输入客户名称、联系人姓名"/>
      <van-icon name="search" class="search-icon" @click="resetAndLoad"/>
    </div>

    <!-- 筛选行 -->
    <div class="filters">
      <div class="filter" @click="openFilter('category')">
        <span>客户分类</span>
        <van-icon name="arrow-down"/>
      </div>
      <div class="filter" @click="openFilter('industry')">
        <span>对应项目</span>
        <van-icon name="arrow-down"/>
      </div>
      <div class="filter" @click="openFilter('dealStatus')">
        <span>成交状态</span>
        <van-icon name="arrow-down"/>
      </div>
      <div class="filter" @click="openFilter('owner')">
        <span>对接销售</span>
        <van-icon name="arrow-down"/>
      </div>
    </div>

    <!-- 排序行 -->
    <div class="sort-bar">
      <span class="sort-label">排序：</span>
      <span :class="['sort-item', sortKey==='create_time'?'active':'']" @click="changeSort('create_time')">
        创建时间<span v-if="sortKey==='create_time'">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
      </span>
      <span :class="['sort-item', sortKey==='deal_money'?'active':'']" @click="changeSort('deal_money')">
        成交金额<span v-if="sortKey==='deal_money'">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
      </span>
      <span :class="['sort-item', sortKey==='value'?'active':'']" @click="changeSort('value')">
        客户价值<span v-if="sortKey==='value'">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
      </span>
      <span :class="['sort-item', sortKey==='name'?'active':'']" @click="changeSort('name')">
        客户名称<span v-if="sortKey==='name'">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
      </span>
    </div>

    <!-- 数据统计（type=value / type=count 时展示） -->
    <div class="stats" v-if="statsData">
      <div class="stats-summary">
        <div class="stat-item">
          <div class="stat-num">{{ statsData.total || 0 }}</div>
          <div class="stat-label">客户数量</div>
        </div>
        <div class="stat-item">
          <div class="stat-num highlight">{{ formatValue(statsData.totalValue) }}</div>
          <div class="stat-label">客户价值(万)</div>
        </div>
        <div class="stat-item">
          <div class="stat-num quota">{{ formatValue(statsData.totalQuota) }}</div>
          <div class="stat-label">客户额度(万)</div>
        </div>
      </div>
      <div class="stats-cats" v-if="statsData.byCategory && statsData.byCategory.length">
        <div class="stats-cats-title">各类别数量 / 价值(万) / 额度(万)</div>
        <div class="cat-chips">
          <div class="cat-chip" v-for="c in statsData.byCategory" :key="c.category">
            <span class="cat-dot" :style="{ background: c.color }"></span>
            <span class="cat-name">{{ c.categoryName || (c.category + '类') }}</span>
            <span class="cat-count">{{ c.count }}</span>
            <span class="cat-value">{{ formatValue(c.value) }}</span>
            <span class="cat-quota">{{ formatValue(c.quota) }}</span>
          </div>
        </div>
      </div>
      <div class="stats-deal" v-if="statsData.byDealStatus && statsData.byDealStatus.length">
        <span class="deal-chip" v-for="d in statsData.byDealStatus" :key="d.dealStatus"
              :class="d.dealStatus === 1 ? 'done' : 'undone'">
          {{ d.label }}：{{ d.count }}
        </span>
      </div>
    </div>

    <!-- 列表 -->
    <div class="list">
      <div v-if="loading" class="loading">加载中...</div>
      <div v-else-if="items.length===0" class="empty">暂无客户数据</div>
      <div v-else class="cards">
        <div class="card" v-for="r in items" :key="r.id">
          <div class="card-side" :style="{ background: r.categoryColor }">
            <span>{{ r.category }}类</span>
          </div>
          <div class="card-body">
            <div class="card-name">{{ r.name || '未命名客户' }}</div>
            <div class="card-line">{{ r.industryName || '未指定项目' }}</div>
            <div class="card-line">联系人：{{ r.contacts || '—' }}</div>
            <div class="card-line">对接销售：{{ r.ownerUserName || '—' }}</div>
            <div class="card-line">创建人：{{ r.creatorName || '—' }}</div>
          <div class="card-line card-remark">备注：{{ r.remark || '—' }}</div>
          </div>
          <div class="card-right">
            <div class="card-money">{{ formatMoney(r.dealMoney) }}</div>
            <div class="card-value">价值：{{ formatValue(r.value) }}</div>
            <div :class="['card-status', dealStatusClass(r.dealStatus)]">
              {{ dealStatusText(r.dealStatus) }}
            </div>
          </div>
          <!-- 操作按钮行 -->
          <div class="card-actions">
            <van-button size="mini" type="primary" plain @click="openDetail(r)">查看详情</van-button>
            <van-button size="mini" plain @click="openEdit(r)">修改</van-button>
            <van-button size="mini" type="warning" plain @click="openFollowAdd(r)">添加跟进记录</van-button>
            <van-button size="mini" type="info" plain @click="openFollowList(r)">查看跟进记录</van-button>
          </div>
        </div>
        <div v-if="items.length < total" class="loadmore" @click="loadMore">加载更多</div>
        <div v-else-if="items.length>0" class="loadmore disabled">— 已加载全部 —</div>
      </div>
    </div>

    <!-- 筛选弹窗 -->
    <van-popup v-model="filterVisible" position="bottom" round :style="{ height: '60%' }">
      <div class="filter-popup">
        <div class="filter-popup-title">
          <span @click="cancelFilter">取消</span>
          <span>{{ filterTitle }}</span>
          <span @click="confirmFilter" class="ok">确定</span>
        </div>
        <div class="filter-search" v-if="filterType !== 'dealStatus'">
          <input v-model="filterSearch" type="text" placeholder="搜索名称" />
        </div>
        <div class="filter-options">
          <template v-if="filterType === 'owner'">
            <template v-for="g in ownerGroupsFiltered">
              <div class="option-group-title">{{ g.dept || '未分组' }}</div>
              <div v-for="o in g.users" :key="o.value"
                   :class="['option', isSelected(o)?'active':'']"
                   @click="toggleOption(o)">
                {{ o.label }}
              </div>
            </template>
            <div v-if="ownerGroupsFiltered.length === 0" class="empty-options">无匹配选项</div>
          </template>
          <template v-else>
            <div v-if="currentOptions.length === 0" class="empty-options">无匹配选项</div>
            <div v-for="o in currentOptions" :key="o.value"
                 :class="['option', isSelected(o)?'active':'']"
                 @click="toggleOption(o)">
              {{ o.label }}
            </div>
          </template>
        </div>
      </div>
    </van-popup>

    <!-- 详情弹窗 -->
    <van-popup v-model="detailVisible" round closeable :style="{ width: '85%' }">
      <div class="detail" v-if="detail">
        <div class="detail-row"><span class="lbl">客户名称：</span><span>{{ detail.name || '—' }}</span></div>
        <div class="detail-row"><span class="lbl">客户分类：</span><span>{{
            detail.category
          }}{{ detail.categoryName ? ('（' + detail.categoryName + '）') : '' }}</span></div>
        <div class="detail-row"><span class="lbl">对接销售：</span><span>{{ detail.ownerUserName || detail.creatorName || detail.creator || '—' }}</span></div>
        <div class="detail-row"><span class="lbl">联系人：</span><span>{{ detail.contacts || '—' }}</span></div>
        <div class="detail-row"><span class="lbl">手机：</span><span>{{ detail.mobile || '—' }}</span></div>
        <div class="detail-row"><span class="lbl">对应项目：</span><span>{{ detail.industryName || '—' }}</span></div>
        <div class="detail-row"><span class="lbl">所属城市：</span><span>{{ detail.areaName || '—' }}</span></div>
        <div class="detail-row"><span class="lbl">详细地址：</span><span>{{ detail.detailAddress || '—' }}</span></div>
        <div class="detail-row"><span class="lbl">成交状态：</span><span :class="dealStatusClass(detail.dealStatus)">{{
            dealStatusText(detail.dealStatus)
          }}</span></div>
        <div class="detail-row"><span class="lbl">成交金额：</span><span>¥ {{ formatMoney(detail.dealMoney) }}</span></div>
        <div class="detail-row"><span class="lbl">预计回款日期：</span><span>{{ formatDate(detail.expectedPaymentTime) }}</span></div>
        <div class="detail-row remark"><span class="lbl">备注：</span><span>{{ detail.remark || '—' }}</span></div>
        <div class="detail-row"><span class="lbl">客户需求：</span><span>{{ detail.customerNeed || '—' }}</span></div>
      </div>
    </van-popup>

    <!-- 修改弹窗 -->
    <van-popup v-model="editVisible" position="bottom" round :style="{ height: '88%' }">
      <div class="edit-popup">
        <div class="edit-title">修改客户信息</div>
        <div class="edit-body">
          <van-field v-model="editForm.name" label="客户名称" placeholder="请输入客户名称" required/>
          <van-field
            v-model="editCategoryLabel"
            label="客户分类"
            placeholder="请选择"
            readonly clickable @click="openEditPicker('category')"
          />
          <van-field
            v-model="editOwnerLabel"
            label="对接销售"
            placeholder="请选择对接销售"
            readonly clickable @click="openEditPicker('owner')"
          />
          <van-field v-model="editForm.contacts" label="联系人" placeholder="请输入联系人"/>
          <van-field v-model="editForm.mobile" label="手机" placeholder="请输入手机号"/>
          <van-field
            v-model="editProvinceName"
            label="所属城市"
            placeholder="请选择省份"
            readonly clickable @click="openEditAreaPicker('province')"
          />
          <van-field
            v-model="editCityName"
            placeholder="请选择城市"
            readonly clickable @click="openEditAreaPicker('city')"
          />
          <van-field
            v-model="editDistrictName"
            placeholder="请选择区/县"
            readonly clickable @click="openEditAreaPicker('district')"
          />
          <van-field v-model="editForm.detailAddress" label="详细地址" placeholder="请输入详细地址"/>
          <van-field
            v-model="editIndustryLabel"
            label="所属项目"
            placeholder="请选择"
            readonly clickable @click="openEditPicker('industry')"
          />
          <van-field
            v-model="editNeedLabel"
            label="客户需求"
            :placeholder="editForm.industryId ? '请选择客户需求' : '请先选择所属项目'"
            readonly clickable @click="openEditPicker('need')"
          />
          <van-cell title="成交状态">
            <van-radio-group v-model="editForm.dealStatus" direction="horizontal">
              <van-radio :name="0">未成交</van-radio>
              <van-radio :name="1">已成交</van-radio>
            </van-radio-group>
          </van-cell>
          <van-field v-model="editForm.dealMoney" label="成交金额" placeholder="万元" type="number"/>
          <van-field v-model="editForm.expectedPaymentTime" label="预计回款" placeholder="日期" type="date"/>
          <van-field v-model="editForm.remark" label="备注" type="textarea" rows="2"/>
        </div>
        <div class="edit-footer">
          <van-button block type="primary" :loading="editSaving" @click="submitEdit">保存</van-button>
        </div>
      </div>
    </van-popup>

    <!-- 修改弹窗内的下拉 -->
    <van-popup v-model="editPickerVisible" position="bottom" round>
      <van-picker
        show-toolbar :columns="editPickerColumns" value-key="label"
        :title="editPickerTitle"
        @confirm="onEditPickerConfirm" @cancel="editPickerVisible=false"
      />
    </van-popup>

    <!-- 修改弹窗内的城市级联 -->
    <van-popup v-model="editAreaPickerVisible" position="bottom" round>
      <van-picker
        show-toolbar :columns="editAreaPickerColumns" value-key="name"
        :title="editAreaPickerTitle"
        @confirm="onEditAreaConfirm" @cancel="editAreaPickerVisible=false"
      />
    </van-popup>

    <!-- 增加跟进记录弹窗 -->
    <van-popup v-model="followAddVisible" position="bottom" round :style="{ height: '90%' }">
      <div class="follow-popup">
        <div class="follow-title">增加跟进记录</div>
        <div class="follow-body">
          <van-field
            v-model="followForm.content"
            label="支持内容"
            type="textarea"
            rows="4"
            placeholder="请输入支持内容"
            required
          />
          <van-field
            v-model="followForm.nextTime"
            label="下次预约"
            type="datetime-local"
            placeholder="预约下次沟通时间"
          />
          <van-cell title="聊天截图" class="upload-cell">
            <template #label>
              <van-uploader
                v-model="followPics"
                :after-read="onFollowPicRead"
                :before-read="beforeFollowPicRead"
                multiple
                :max-count="6"
                upload-text="上传截图"
              />
            </template>
          </van-cell>
        </div>
        <div class="follow-footer">
          <van-button block type="primary" :loading="followSaving" @click="submitFollow">提交</van-button>
        </div>
      </div>
    </van-popup>

    <!-- 查看跟进记录弹窗 -->
    <van-popup v-model="followListVisible" round closeable :style="{ width: '90%', maxHeight: '80%' }">
      <div class="follow-list">
        <div class="follow-list-title">跟进记录（时间倒序）</div>
        <div v-if="followList.length===0" class="follow-empty">暂无跟进记录</div>
        <div v-for="f in followList" :key="f.id" class="follow-item">
          <div class="follow-item-head">
            <span class="follow-time">{{ formatDateTime(f.createTime) }}</span>
            <span class="follow-contact">联系人：{{ f.contactIds || '—' }}</span>
          </div>
          <div class="follow-content">{{ f.content }}</div>
          <div class="follow-next" v-if="f.nextTime">下次预约：{{ formatDateTime(f.nextTime) }}</div>
          <div class="follow-pics" v-if="followPicList(f).length">
            <img v-for="(p,i) in followPicList(f)" :key="i" :src="p" class="follow-pic"
                 @click="previewPics(followPicList(f), i)"/>
          </div>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import coreapi from "@/utils/coreapi";
import {ImagePreview} from "vant";

// 客户需求选项：随所属项目（行业）变化（与新增页保持一致）
const CUSTOMER_NEED_OPTIONS = {
  "健康": ["小房型", "大房型", "理疗", "心理"],
  "安全": ["城市代理", "网红招募", "渠道"],
  "传媒": ["城市代理", "主任委员", "会员招募", "渠道"]
};

// 与新增页保持一致的城市数据（用于修改时选择）
const CITY_DATA = [
  {
    name: "北京市", code: "110000", children: [
      {
        name: "北京市", code: "110100", children: [
          {name: "东城区", code: "110101"}, {name: "西城区", code: "110102"},
          {name: "朝阳区", code: "110105"}, {name: "丰台区", code: "110106"},
          {name: "海淀区", code: "110108"}, {name: "通州区", code: "110112"}
        ]
      }
    ]
  },
  {
    name: "上海市", code: "310000", children: [
      {
        name: "上海市", code: "310100", children: [
          {name: "黄浦区", code: "310101"}, {name: "徐汇区", code: "310104"},
          {name: "浦东新区", code: "310115"}
        ]
      }
    ]
  },
  {
    name: "广东省", code: "440000", children: [
      {
        name: "广州市", code: "440100", children: [
          {name: "天河区", code: "440106"}, {name: "番禺区", code: "440113"}
        ]
      },
      {
        name: "深圳市", code: "440300", children: [
          {name: "福田区", code: "440304"}, {name: "南山区", code: "440305"}
        ]
      }
    ]
  }
];

export default {
  name: "BossCustomerList",
  data() {
    return {
      creator: localStorage.getItem("CrmUserId") || "",
      keyword: "",
      items: [],
      total: 0,
      page: 1,
      size: 20,
      sortKey: "create_time",
      sortDir: "desc",
      loading: false,
      filterOptions: {categories: [], industries: [], owners: []},
      filterCategory: [],
      filterIndustry: [],
      filterDealStatus: [],
      filterOwner: [],
      filterVisible: false,
      filterType: "",
      filterSearch: "",
      tempSelection: [],
      detailVisible: false,
      detail: null,
      // 修改
      editVisible: false,
      editSaving: false,
      editTarget: null,
      editForm: {},
      editPicker: "",
      editPickerVisible: false,
      editOwners: [],
      // 编辑表单的城市级联
      editProvinces: CITY_DATA,
      editCities: [],
      editDistricts: [],
      editProvinceName: "",
      editCityName: "",
      editDistrictName: "",
      editAreaPickerVisible: false,
      editAreaPickerType: "",
      editAreaPickerColumns: [],
      editAreaPickerTitle: "",
      // 沟通
      commSheetVisible: false,
      commTarget: null,
      commActions: [
        {name: "增加跟进记录", key: "add"},
        {name: "查看跟进记录", key: "list"}
      ],
      followAddVisible: false,
      followSaving: false,
      followForm: {contactIds: "", content: "", nextTime: "", picUrls: ""},
      followPics: [],
      followListVisible: false,
      followList: [],
      // 数据统计（type=value / type=count 时展示在列表上方）
      statsData: null
    };
  },
  computed: {
    // owner 筛选选项为按部门分组的结构 [{dept, users}]，这里在搜索关键字过滤后返回同样的分组结构
    ownerGroupsFiltered() {
      const groups = this.filterOptions.owners || [];
      const kw = (this.filterSearch || "").trim().toLowerCase();
      return groups
        .map(g => {
          const users = (g.users || []).filter(u => {
            if (!kw) return true;
            const label = u.label != null ? String(u.label) : "";
            const name = u.name != null ? String(u.name) : "";
            return label.toLowerCase().includes(kw) || name.toLowerCase().includes(kw);
          });
          return {dept: g.dept, users};
        })
        .filter(g => g.users.length > 0);
    },
    filterTitle() {
      return {
        category: "客户分类", industry: "对应项目",
        dealStatus: "成交状态", owner: "对接销售"
      }[this.filterType] || "筛选";
    },
    currentOptions() {
      let opts = [];
      if (this.filterType === "dealStatus") {
        opts = [{value: 1, label: "已成交"}, {value: 0, label: "未成交"}];
      } else if (this.filterType === "category") {
        opts = this.filterOptions.categories || [];
      } else if (this.filterType === "industry") {
        opts = this.filterOptions.industries || [];
      } else if (this.filterType === "owner") {
        opts = this.filterOptions.owners || [];
      }
      const kw = (this.filterSearch || "").trim();
      if (!kw) return opts;
      const lower = kw.toLowerCase();
      return opts.filter(o => {
        const label = o.label != null ? String(o.label) : "";
        const name = o.name != null ? String(o.name) : "";
        return label.toLowerCase().includes(lower) || name.toLowerCase().includes(lower);
      });
    },
    editCategoryLabel() {
      const c = this.filterOptions.categories.find(x => String(x.value) === String(this.editForm.categoryId));
      return c ? c.label : "";
    },
    editIndustryLabel() {
      const i = this.filterOptions.industries.find(x => String(x.value) === String(this.editForm.industryId));
      return i ? i.label : "";
    },
    editNeedLabel() {
      return this.editForm.customerNeed || "";
    },
    editOwnerList() {
      // editOwners 为按部门分组的结构 [{dept, users}]，这里展平为扁平列表供匹配/选择器使用
      const groups = this.editOwners || [];
      const list = [];
      groups.forEach(g => (g.users || []).forEach(u => list.push(u)));
      return list;
    },
    editOwnerLabel() {
      const o = this.editOwnerList.find(x => String(x.id) === String(this.editForm.ownerUserId));
      return o ? o.name : "";
    },
    editPickerColumns() {
      if (this.editPicker === "category") return this.filterOptions.categories || [];
      if (this.editPicker === "owner") {
        return (this.editOwnerList || []).map(o => ({...o, label: o.dept ? o.name + "（" + o.dept + "）" : o.name}));
      }
      if (this.editPicker === "industry") return this.filterOptions.industries || [];
      if (this.editPicker === "need") {
        const opts = CUSTOMER_NEED_OPTIONS[this.editIndustryLabel] || [];
        return opts.map(o => ({label: o, value: o}));
      }
      return [];
    },
    editPickerTitle() {
      return {category: "选择客户分类", owner: "选择对接销售", industry: "选择所属项目", need: "选择客户需求"}[this.editPicker] || "";
    }
  },
  async mounted() {
    const t = this.$route.query.type;
    if (t === "value") {
      this.sortKey = "value";
      this.sortDir = "desc";
    }
    await this.load(true);
    try {
      this.editOwners = await coreapi.bossOwners();
    } catch (e) {
      console.error("加载对接销售列表失败", e);
    }
  },
  methods: {
    isSelected(o) {
      return this.tempSelection.some(s => String(s) === String(o.value));
    },
    currentSelection() {
      return this.tempSelection;
    },
    toggleOption(o) {
      const sel = this.tempSelection;
      const idx = sel.findIndex(s => String(s) === String(o.value));
      if (idx >= 0) sel.splice(idx, 1); else sel.push(o.value);
    },
    openFilter(type) {
      this.filterType = type;
      this.filterSearch = "";
      if (type === "category") this.tempSelection = [...this.filterCategory];
      else if (type === "industry") this.tempSelection = [...this.filterIndustry];
      else if (type === "dealStatus") this.tempSelection = [...this.filterDealStatus];
      else if (type === "owner") this.tempSelection = [...this.filterOwner];
      else this.tempSelection = [];
      this.filterVisible = true;
    },
    cancelFilter() {
      this.filterVisible = false;
      this.filterSearch = "";
    },
    confirmFilter() {
      if (this.filterType === "category") this.filterCategory = [...this.tempSelection];
      else if (this.filterType === "industry") this.filterIndustry = [...this.tempSelection];
      else if (this.filterType === "dealStatus") this.filterDealStatus = [...this.tempSelection];
      else if (this.filterType === "owner") this.filterOwner = [...this.tempSelection];
      this.filterVisible = false;
      this.filterSearch = "";
      this.resetAndLoad();
    },
    changeSort(key) {
      if (this.sortKey === key) this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
      else {
        this.sortKey = key;
        this.sortDir = "desc";
      }
      this.resetAndLoad();
    },
    resetAndLoad() {
      this.page = 1;
      this.load(true);
    },
    loadMore() {
      this.page += 1;
      this.load(false);
    },
    async load(reset) {
      if (this.loading) return;
      this.loading = true;
      try {
        const params = {
          creator: this.creator,
          keyword: this.keyword || undefined,
          category: this.filterCategory.length ? this.filterCategory.join(",") : undefined,
          industry: this.filterIndustry.length ? this.filterIndustry.join(",") : undefined,
          dealStatus: this.filterDealStatus.length ? this.filterDealStatus[0] : undefined,
          owner: this.filterOwner.length ? this.filterOwner.join(",") : undefined,
          sortKey: this.sortKey,
          sortDir: this.sortDir,
          page: this.page,
          size: this.size,
          // type=value 与 type=count 均请求后端返回统计数据
          stats: (this.$route.query.type === "value" || this.$route.query.type === "count") ? 1 : undefined
        };
        const data = await coreapi.bossCustomerList(params);
        this.total = data.total || 0;
        if (data.filterOptions) this.filterOptions = data.filterOptions;
        if (reset) this.items = data.items || [];
        else this.items = this.items.concat(data.items || []);
        // type=value 与 type=count 同步加载列表上方的数据统计（参照 count 样式）
        this.statsData = data.stats || null;
      } catch (e) {
        console.error("加载客户列表失败", e);
      } finally {
        this.loading = false;
      }
    },
    openDetail(r) {
      this.detail = r;
      this.detailVisible = true;
    },
    goAdd() {
      this.$router.push({path: "/boss/customer-add", query: {from: "list", type: this.$route.query.type}});
    },
    // —— 修改 ——
    openEdit(r) {
      this.editTarget = r;
      const code = r.areaId != null ? String(r.areaId) : "";
      // 根据已存 areaId 反查省/市/区名称
      this.editProvinceName = "";
      this.editCityName = "";
      this.editDistrictName = "";
      this.editCities = [];
      this.editDistricts = [];
      if (code && code !== "0") {
        for (const p of CITY_DATA) {
          for (const c of (p.children || [])) {
            const d = (c.children || []).find(x => x.code === code);
            if (d) {
              this.editProvinceName = p.name;
              this.editCityName = c.name;
              this.editDistrictName = d.name;
              this.editCities = p.children || [];
              this.editDistricts = c.children || [];
              break;
            }
          }
          if (this.editDistrictName) break;
        }
      }
      this.editForm = {
        id: r.id,
        name: r.name,
        categoryId: r.categoryId != null ? r.categoryId : (r.categoryName ? this.findValue(this.filterOptions.categories, r.categoryName) : ""),
        industryId: r.industryId,
        ownerUserId: r.ownerUserId != null && r.ownerUserId !== "" ? r.ownerUserId : r.creator,
        contacts: r.contacts,
        mobile: r.mobile,
        areaId: code && code !== "0" ? code : "",
        detailAddress: r.detailAddress || "",
        dealStatus: r.dealStatus,
        dealMoney: r.dealMoney,
        expectedPaymentTime: this.formatDateValue(r.expectedPaymentTime),
        remark: r.remark,
        customerNeed: r.customerNeed || ""
      };
      this.editVisible = true;
    },
    findValue(list, label) {
      const f = (list || []).find(x => x.label === label);
      return f ? f.value : "";
    },
    openEditAreaPicker(type) {
      this.editAreaPickerType = type;
      if (type === "province") {
        this.editAreaPickerColumns = this.editProvinces;
        this.editAreaPickerTitle = "选择省份";
      } else if (type === "city") {
        if (!this.editProvinceName) {
          this.$toast("请先选择省份");
          return;
        }
        this.editAreaPickerColumns = this.editCities;
        this.editAreaPickerTitle = "选择城市";
      } else if (type === "district") {
        if (!this.editCityName) {
          this.$toast("请先选择城市");
          return;
        }
        this.editAreaPickerColumns = this.editDistricts;
        this.editAreaPickerTitle = "选择区/县";
      }
      this.editAreaPickerVisible = true;
    },
    onEditAreaConfirm(value) {
      if (this.editAreaPickerType === "province") {
        this.editProvinceName = value.name;
        const p = this.editProvinces.find(x => x.name === value.name);
        this.editCities = p && p.children ? p.children : [];
        this.editCityName = "";
        this.editDistrictName = "";
        this.editDistricts = [];
        this.editForm.areaId = "";
      } else if (this.editAreaPickerType === "city") {
        this.editCityName = value.name;
        const c = this.editCities.find(x => x.name === value.name);
        this.editDistricts = c && c.children ? c.children : [];
        this.editDistrictName = "";
        this.editForm.areaId = "";
      } else if (this.editAreaPickerType === "district") {
        this.editDistrictName = value.name;
        this.editForm.areaId = value.code || "";
      }
      this.editAreaPickerVisible = false;
    },
    openEditPicker(type) {
      if (type === "need" && !this.editForm.industryId) {
        this.$toast("请先选择所属项目");
        return;
      }
      this.editPicker = type;
      this.editPickerVisible = true;
    },
    onEditPickerConfirm(value) {
      if (this.editPicker === "category") this.editForm.categoryId = value.value;
      else if (this.editPicker === "owner") this.editForm.ownerUserId = value.id;
      else if (this.editPicker === "industry") {
        this.editForm.industryId = value.value;
        this.editForm.customerNeed = "";
      } else if (this.editPicker === "need") this.editForm.customerNeed = value.value;
      this.editPickerVisible = false;
    },
    async submitEdit() {
      if (!this.editForm.name || !this.editForm.name.trim()) {
        this.$toast("客户名称不能为空");
        return;
      }
      this.editSaving = true;
      try {
        const payload = {
          id: this.editForm.id,
          name: this.editForm.name.trim(),
          categoryId: this.editForm.categoryId,
          industryId: this.editForm.industryId,
          ownerUserId: this.editForm.ownerUserId,
          contacts: this.editForm.contacts,
          mobile: this.editForm.mobile,
          areaId: this.editForm.areaId,
          detailAddress: this.editForm.detailAddress,
          dealStatus: this.editForm.dealStatus,
          dealMoney: this.editForm.dealMoney,
          expectedPaymentTime: this.editForm.expectedPaymentTime,
          customerNeed: this.editForm.customerNeed,
          remark: this.editForm.remark,
          updater: this.creator || localStorage.getItem("UserSysno") || 0
        };
        const res = await coreapi.updateBossCustomer(payload);
        if (res && res.success === false) {
          throw new Error("保存失败：数据未更新（记录可能已被删除）");
        }
        this.$toast.success("修改成功");
        this.editVisible = false;
        this.resetAndLoad();
      } catch (e) {
        this.$toast.fail(e.message || "保存失败");
      } finally {
        this.editSaving = false;
      }
    },
    // —— 跟进记录 ——
    openFollowAdd(r) {
      this.commTarget = r;
      this.followForm = {
        contactIds: r.contactId ? String(r.contactId) : "",
        content: "",
        nextTime: "",
        picUrls: ""
      };
      this.followPics = [];
      this.followAddVisible = true;
    },
    beforeFollowPicRead(file) {
      const ok = /\.(png|jpe?g|gif|webp|bmp)$/i.test(file.name || "");
      if (!ok) this.$toast("仅支持图片格式");
      return ok;
    },
    async onFollowPicRead(item) {
      const file = item.file;
      const fd = new FormData();
      fd.append("file", file);
      item.status = "uploading";
      item.message = "上传中";
      try {
        const res = await coreapi.uploadBossFollowUpImage(fd);
        item.url = res.url;
        item.status = "done";
        item.message = "";
      } catch (e) {
        item.status = "failed";
        item.message = "上传失败";
        this.$toast.fail("截图上传失败");
      }
    },
    async submitFollow() {
      if (!this.followForm.content || !this.followForm.content.trim()) {
        this.$toast("支持内容不能为空");
        return;
      }
      const urls = this.followPics.map(p => p.url).filter(Boolean);
      let nextTs = null;
      if (this.followForm.nextTime) {
        nextTs = Math.floor(new Date(this.followForm.nextTime.replace("T", " ")).getTime() / 1000);
      }
      this.followSaving = true;
      try {
        await coreapi.addBossFollowUp({
          customerId: this.commTarget.id,
          contactIds: this.followForm.contactIds,
          content: this.followForm.content.trim(),
          nextTime: nextTs,
          picUrls: JSON.stringify(urls),
          creator: this.creator || localStorage.getItem("UserSysno") || 0
        });
        this.$toast.success("提交成功");
        this.followAddVisible = false;
      } catch (e) {
        this.$toast.fail(e.message || "提交失败");
      } finally {
        this.followSaving = false;
      }
    },
    async openFollowList(r) {
      try {
        this.followList = await coreapi.bossFollowUps(
          r.id, r.contactId ? String(r.contactId) : ""
        );
        this.followListVisible = true;
      } catch (e) {
        this.$toast.fail("加载跟进记录失败");
      }
    },
    followPicList(f) {
      try {
        const arr = JSON.parse(f.picUrls || "[]");
        return Array.isArray(arr) ? arr : [];
      } catch (e) {
        return [];
      }
    },
    previewPics(list, index) {
      if (!list || !list.length) return;
      ImagePreview({images: list, startPosition: index || 0});
    },
    formatMoney(v) {
      if (v === null || v === undefined) return "0.00万";
      return Number(v).toFixed(2) + "万";
    },
    formatValue(v) {
      const n = Number(v);
      if (!isFinite(n)) return "0";
      return n.toFixed(2) ;
    },
    // 成交状态完全由 deal_status 字段驱动展示
    dealStatusText(status) {
      return (status === 1 || status === "1" || status === true) ? "已成交" : "未成交";
    },
    dealStatusClass(status) {
      return (status === 1 || status === "1" || status === true) ? "done" : "undone";
    },
    formatDate(v) {
      if (!v) return "—";
      const s = String(v);
      return s.length >= 10 ? s.substring(0, 10) : s;
    },
    // 将后端返回的日期（可能是 Date 对象或字符串）规整为 yyyy-MM-dd，用于编辑表单的 date 输入
    formatDateValue(v) {
      if (!v) return "";
      if (v instanceof Date) {
        const pad = n => String(n).padStart(2, "0");
        return `${v.getFullYear()}-${pad(v.getMonth() + 1)}-${pad(v.getDate())}`;
      }
      const s = String(v);
      return s.length >= 10 ? s.substring(0, 10) : s;
    },
    formatDateTime(v) {
      if (!v) return "—";
      const s = String(v);
      return s.length >= 19 ? s.substring(0, 19).replace("T", " ") : s;
    }
  }
};
</script>

<style scoped>
.page {
  background: #f5f6f8;
  min-height: 100vh;
  padding-bottom: 24px;
}

.search-bar {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background: #fff;
  border-bottom: 1px solid #eee;
}

.search-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  font-size: 13px;
  outline: none;
  background: #fafafa;
}

.search-icon {
  margin-left: 10px;
  font-size: 20px;
  color: #666;
}

.filters {
  display: flex;
  background: #fff;
  border-bottom: 1px solid #eee;
}

.filter {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 13px;
  color: #333;
  gap: 2px;
}

.sort-bar {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #fff;
  border-bottom: 1px solid #eee;
  font-size: 12px;
  color: #888;
}

.sort-label {
  margin-right: 6px;
}

.sort-item {
  margin-right: 14px;
  color: #666;
}

.sort-item.active {
  color: #1989fa;
  font-weight: 600;
}

/* 数据统计面板 */
.stats {
  margin: 8px 12px 0;
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.stats-summary {
  display: flex;
  gap: 10px;
}

.stat-item {
  flex: 1;
  background: #f7f8fa;
  border-radius: 6px;
  padding: 12px 0;
  text-align: center;
}

.stat-num {
  font-size: 22px;
  font-weight: 700;
  color: #333;
  line-height: 1.2;
}

.stat-num.highlight {
  color: #fa8c16;
}

.stat-num.quota {
  color: #1890ff;
}

.stat-label {
  margin-top: 4px;
  font-size: 12px;
  color: #999;
}

.stats-cats {
  margin-top: 12px;
}

.stats-cats-title {
  font-size: 12px;
  color: #888;
  margin-bottom: 8px;
}

.cat-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.cat-chip {
  display: flex;
  align-items: center;
  background: #f7f8fa;
  border-radius: 14px;
  padding: 4px 10px;
  font-size: 12px;
  color: #555;
}

.cat-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
}

.cat-name {
  margin-right: 6px;
}

.cat-count {
  font-weight: 600;
  color: #333;
  margin-right: 2px;
}

.cat-count::after {
  content: "个";
  font-weight: 400;
  color: #999;
  margin-left: 1px;
}

.cat-value {
  color: #fa8c16;
  margin-left: 4px;
}

.cat-value::before {
  content: "¥";
  margin-right: 1px;
}

.cat-quota {
  color: #1890ff;
  margin-left: 4px;
}

.cat-quota::before {
  content: "额";
  margin-right: 1px;
}

.stats-deal {
  margin-top: 12px;
  display: flex;
  gap: 8px;
}

.deal-chip {
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 12px;
  color: #fff;
}

.deal-chip.done {
  background: #52c41a;
}

.deal-chip.undone {
  background: #ee5b4f;
}

.list {
  padding: 10px 12px;
}

.loading, .empty {
  text-align: center;
  color: #999;
  padding: 30px 0;
  font-size: 13px;
}

.cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.card {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.card-side {
  width: 60px;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
}

.card-body {
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
}

.card-name {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.card-line {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
}

.card-remark {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-all;
}

.card-right {
  width: 132px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  padding: 10px 12px;
}

.card-money {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.card-value {
  font-size: 12px;
  color: #fa8c16;
  margin-bottom: 6px;
  white-space: nowrap;
}

.card-status {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 10px;
  color: #fff;
}

.card-status.done {
  background: #52c41a;
}

.card-status.undone {
  background: #ee5b4f;
}

.card-actions {
  width: 100%;
  display: flex;
  gap: 8px;
  padding: 8px 12px 10px 12px;
  border-top: 1px solid #f0f0f0;
  justify-content: flex-end;
  background: #fafafa;
}

.loadmore {
  text-align: center;
  padding: 14px 0;
  color: #1989fa;
  font-size: 13px;
}

.loadmore.disabled {
  color: #bbb;
}

.filter-popup {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.filter-popup-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  font-size: 14px;
}

.filter-popup-title .ok {
  color: #1989fa;
}

.filter-search {
  padding: 10px 16px 0;
}
.filter-search input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  font-size: 13px;
  outline: none;
  background: #fafafa;
}
.empty-options {
  width: 100%;
  text-align: center;
  padding: 30px 0;
  color: #999;
  font-size: 13px;
}

.filter-options {
  padding: 14px 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  overflow-y: auto;
}

.option {
  padding: 6px 14px;
  border: 1px solid #ddd;
  border-radius: 16px;
  font-size: 13px;
  color: #555;
}

.option.active {
  border-color: #1989fa;
  background: #e6f0ff;
  color: #1989fa;
}

.option-group-title {
  width: 100%;
  padding: 8px 14px 4px;
  font-size: 12px;
  font-weight: 600;
  color: #999;
  background: #f5f6f8;
}

.detail {
  padding: 24px 18px 18px;
  font-size: 13px;
  color: #333;
  max-height: 70vh;
  overflow-y: auto;
}

.detail-row {
  display: flex;
  margin-bottom: 8px;
  line-height: 1.7;
}

.detail-row .lbl {
  color: #888;
  width: 96px;
  flex-shrink: 0;
  text-align: right;
  padding-right: 8px;
}

.detail-row.remark span:last-child {
  white-space: pre-wrap;
  word-break: break-all;
}

.edit-popup, .follow-popup {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.edit-title, .follow-title {
  padding: 14px 16px;
  font-size: 15px;
  font-weight: 600;
  text-align: center;
  border-bottom: 1px solid #eee;
}

.edit-body, .follow-body {
  flex: 1;
  overflow-y: auto;
  padding: 4px 0;
}

.edit-footer, .follow-footer {
  padding: 10px 16px 16px;
}

.follow-list {
  padding: 24px 16px 16px;
  max-height: 80vh;
  overflow-y: auto;
}

.follow-list-title {
  font-size: 15px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 12px;
}

.follow-empty {
  text-align: center;
  color: #999;
  padding: 30px 0;
}

.follow-item {
  border-bottom: 1px solid #f0f0f0;
  padding: 12px 0;
}

.follow-item-head {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #888;
  margin-bottom: 6px;
}

.follow-time {
  color: #1989fa;
}

.follow-content {
  font-size: 13px;
  color: #333;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-all;
}

.follow-next {
  font-size: 12px;
  color: #fa8c16;
  margin-top: 6px;
}

.follow-pics {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.follow-pic {
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/views/boss/CustomerList.vue